import 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { getApp, initializeApp, FirebaseApp as FirebaseAppType } from 'firebase/app';
import {
  Auth,
  getAuth,
  setPersistence,
  browserLocalPersistence,
  inMemoryPersistence,
  indexedDBLocalPersistence,
  initializeAuth,
  onAuthStateChanged,
  signInWithCustomToken,
  signOut,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import axios from 'axios';
import { sendClientLog } from './client-log';
import httpService from './httpService';
import { Subject } from 'rxjs';

const ENV = (import.meta as any).env;
const DEBUG_LOGS = ENV?.VITE_APP_DEBUG_LOGS === 'true';
const ENABLE_IOS_PERSISTENCE_FIX = ENV?.VITE_ENABLE_IOS_PERSISTENCE_FIX !== 'false';

const firebaseCustomToken = ENV.VITE_APP_FIREBASE_CUSTOM_TOKEN;
const org = ENV.VITE_APP_ORG;

const firebaseConfig = {
  apiKey: ENV.VITE_APP_FIREBASE_KEY,
  authDomain: ENV.VITE_APP_AUTH_DOMAIN,
  projectId: ENV.VITE_APP_PROJECT_ID,
  storageBucket: ENV.VITE_APP_STORAGE_BUCKET,
  messagingSenderId: ENV.VITE_APP_SENDER_ID,
  appId: ENV.VITE_APP_ID,
  measurementId: ENV.VITE_APP_MEASUREMENT_ID,
  tenantId: ENV?.VITE_APP_TENANT || 'plenitude-wjxil',
};

// Check if Firebase is configured (has a valid API key)
// Web admin apps (Clerk-based) don't need Firebase and won't have these env vars
const isFirebaseConfigured = Boolean(firebaseConfig.apiKey);

let FirebaseApp: FirebaseAppType | null = null;
export let auth: Auth | null = null;
export const tokenChange = new Subject<string>();

// Configure web persistence with fallbacks (helps on iOS Chrome)
// Defined outside the if block to avoid strict mode issues
const configurePersistence = () => {
  if (!auth) return;
  // Native already uses indexedDBLocalPersistence via initializeAuth
  if (Capacitor.isNativePlatform()) return;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOSWebKit = /iPhone|iPad|iPod/.test(ua);
  const isCriOS = /CriOS\//.test(ua);
  const tryChain = async () => {
    if (!auth) return;
    try {
      if (ENABLE_IOS_PERSISTENCE_FIX && (isIOSWebKit || isCriOS)) {
        await setPersistence(auth, browserLocalPersistence);
        if (DEBUG_LOGS) try { console.log('[firebase:persistence] browserLocalPersistence (iOS fix)'); sendClientLog({ tag: '[firebase:persistence]', value: 'browserLocalPersistence (iOS fix)' }); } catch(e: any) { console.error(e); }
        return;
      }
      await setPersistence(auth, indexedDBLocalPersistence);
      if (DEBUG_LOGS) try { console.log('[firebase:persistence] indexedDBLocalPersistence'); sendClientLog({ tag: '[firebase:persistence]', value: 'indexedDBLocalPersistence' }); } catch(e: any) { console.error(e); }
    } catch {
      if (!auth) return;
      try {
        await setPersistence(auth, browserLocalPersistence);
        if (DEBUG_LOGS) try { console.log('[firebase:persistence] browserLocalPersistence (fallback)'); sendClientLog({ tag: '[firebase:persistence]', value: 'browserLocalPersistence (fallback)' }); } catch(e: any) { console.error(e); }
      } catch {
        if (!auth) return;
        try {
          await setPersistence(auth, inMemoryPersistence);
          if (DEBUG_LOGS) try { console.log('[firebase:persistence] inMemoryPersistence'); sendClientLog({ tag: '[firebase:persistence]', value: 'inMemoryPersistence' }); } catch(e: any) { console.error(e); }
        } catch {
          if (DEBUG_LOGS) try { console.warn('[firebase:persistence] failed to set any persistence'); sendClientLog({ tag: '[firebase:persistence]', value: 'failed' }); } catch(e: any) { console.error(e); }
        }
      }
    }
  };
  void tryChain();
};

// Only initialize Firebase if configured (mobile apps)
if (isFirebaseConfigured) {
  FirebaseApp = initializeApp(firebaseConfig);
  if (DEBUG_LOGS) {
    try {
      console.log('[firebase:init]', {
        tenantId: firebaseConfig.tenantId,
        isNative: Capacitor.isNativePlatform(),
        ua: typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a',
      });
      sendClientLog({ tag: '[firebase:init]', tenantId: firebaseConfig.tenantId, isNative: Capacitor.isNativePlatform(), ua: typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a' });
    } catch( e: any ) {console.error(e);}
  }

  if (Capacitor.isNativePlatform()) {
    auth = initializeAuth(getApp(), {
      persistence: indexedDBLocalPersistence,
    });
  } else {
    auth = getAuth(FirebaseApp);
  }

  auth.tenantId = firebaseConfig.tenantId;
  configurePersistence();
} else {
  // Firebase not configured - web admin apps using Clerk don't need it
  if (DEBUG_LOGS) {
    console.log('[firebase:skip] Firebase not configured - using Clerk authentication');
  }
}

// One-time readiness gate to wait initial auth hydration
let authInitialized = !isFirebaseConfigured; // Already "ready" if not using Firebase
const authReady: Promise<void> = new Promise((resolve) => {
  if (!auth) {
    // No Firebase auth - resolve immediately
    resolve();
    return;
  }
  onAuthStateChanged(auth, () => {
    if (!authInitialized) {
      authInitialized = true;
      if (DEBUG_LOGS) try { console.log('[firebase:authReady] initial auth event'); sendClientLog({ tag: '[firebase:authReady]' }); } catch(e: any) { console.error(e); }
      resolve();
    }
  });
});

export class FirebaseService {
  async getCustomToken(email: string, password: string) {
    if (!isFirebaseConfigured) {
      console.warn('[FirebaseService] Firebase not configured - use Clerk authentication instead');
      return undefined;
    }
    const endpoint = firebaseCustomToken;
    const data = {
      user: email,
      password: password,
      org: org,
    };

    try {
      const response = await httpService.post({
        url: endpoint,
        data: data,
      });
      return (response as any).data.token;
    } catch (error: any) {
      throw new Error(error.code);
    }
  }

  async signInWithToken(token: string) {
    if (!auth) {
      console.warn('[FirebaseService] Firebase not configured - use Clerk authentication instead');
      return undefined;
    }
    return signInWithCustomToken(auth, token);
  }

  async signInWithUserNameAndPassord(email: string, password: string) {
    if (!auth) {
      console.warn('[FirebaseService] Firebase not configured - use Clerk authentication instead');
      return undefined;
    }
    return signInWithEmailAndPassword(auth, email, password);
  }

  onAuthStateChange(callback: (user: any) => void) {
    if (!auth) {
      // No Firebase auth - call with null user immediately and return no-op unsubscribe
      callback(null);
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }
    return onAuthStateChanged(auth, (user) => {
      if (DEBUG_LOGS) {
        try {
          const payload = {
            hasUser: Boolean(user),
            uid: (user && (user as any).uid) || null,
            email: (user && (user as any).email) || null,
            visibility: typeof document !== 'undefined' ? document.visibilityState : 'n/a',
          };
          console.log('[firebase:onAuthStateChanged]', payload);
          sendClientLog({ tag: '[firebase:onAuthStateChanged]', ...payload });
        } catch(e: any) { console.error(e); }
      }
      callback(user);
    });
  }

  async getIdToken(forceRefresh = false): Promise<string | undefined> {
    if (!auth) {
      // No Firebase auth configured - return undefined (web apps use Clerk tokens)
      return undefined;
    }
    // Wait for initial auth hydration to avoid false "no token" on cold start
    try {
      const withTimeout = <T>(p: Promise<T>, ms: number) => new Promise<T>((resolve) => {
        let settled = false;
        const timer = setTimeout(() => { if (!settled) resolve(undefined as unknown as T); }, ms);
        p.then((v) => { if (!settled) { settled = true; clearTimeout(timer); resolve(v); } }).catch(() => { if (!settled) { settled = true; clearTimeout(timer); resolve(undefined as unknown as T); } });
      });
      await withTimeout(authReady, 3000);
    } catch(e: any) { console.error(e); }

    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken(forceRefresh);
        return token;
      } catch {
        return undefined;
      }
    }
    return undefined;
  }

  // Explicitly refresh the token
  async refreshToken(): Promise<string | undefined> {
    return this.getIdToken(true);
  }

  getCurrentUserEmail(): string | null {
    if (!auth) return null;
    const user = auth.currentUser;
    return user ? user.email : null;
  }

  getCurrentUserDisplayname(): string | null {
    if (!auth) return null;
    const user = auth.currentUser;
    return user ? user.displayName : null;
  }

  async signOut(): Promise<void> {
    if (!auth) {
      // No Firebase auth - nothing to sign out from
      return;
    }
    await signOut(auth);
  }

  setupTokenChangeListener() {
    if (!auth) {
      // No Firebase auth - nothing to listen to
      return;
    }
    auth.onIdTokenChanged(async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken();
          tokenChange.next(token);
          if (DEBUG_LOGS) {
            try {
              const payload = { hasUser: true, uid: (user as any)?.uid || null };
              console.log('[firebase:onIdTokenChanged]', payload);
              sendClientLog({ tag: '[firebase:onIdTokenChanged]', ...payload });
            } catch(e: any) { console.error(e); }
          }
        } catch (error: any) {
          // console.error('Error fetching token:', error);
          console.error(error.message);
        }
      } else {
        // Handle the case when the user is signed out
        tokenChange.next(undefined); //TODO - Z Correctly handle the undefined state if not already done
        if (DEBUG_LOGS) {
          try {
            console.log('[firebase:onIdTokenChanged]', { hasUser: false });
            sendClientLog({ tag: '[firebase:onIdTokenChanged]', hasUser: false });
          } catch(e: any) { console.error(e); }
        }
      }
    });
  }

}
